import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../api';
import { store } from '../../store';
import { t } from '../../i18n';
import { icon } from '../../components/ui/icons';
import { showToast } from '../../components/ui/toast';

interface Ref { id: string; name: string; sort_order: number; }
interface Pass {
  id: string; dance_type_id: string; level_id: string;
  competition_name: string; competition_date: string; notes: string | null;
  dance_type_name?: string; level_name?: string;
}
interface PassForm { id?: string; dance_type_id: string; level_id: string; competition_name: string; competition_date: string; notes: string; }

/**
 * Irish Dance — competition passes (stars) for one student. Shows a dance-type × level
 * matrix of pass status; teachers/admins click a cell to record or edit a pass.
 */
@customElement('cadence-irish-dance-passes')
export class IrishDancePasses extends LitElement {
  @property() studentId = '';
  @state() private types: Ref[] = [];
  @state() private levels: Ref[] = [];
  @state() private passes: Pass[] = [];
  @state() private loading = true;
  @state() private form: PassForm | null = null;
  @state() private saving = false;

  static styles = css`
    :host { display: block; }
    .muted { color: var(--color-text-secondary); }
    .scroll { overflow-x: auto; }
    table { border-collapse: collapse; width: 100%; min-width: 480px; }
    th, td { border: 1px solid var(--color-border); padding: 8px 10px; text-align: center; font-size: 0.9rem; }
    th { background: var(--color-surface-alt, var(--color-bg)); font-weight: 600; }
    th.rowhead, td.rowhead { text-align: left; white-space: nowrap; font-weight: 600; }
    .cell { cursor: default; }
    .cell.editable { cursor: pointer; }
    .cell.editable:hover { background: var(--color-sidebar-hover, rgba(0,0,0,0.04)); }
    .pass { display: inline-flex; align-items: center; gap: 8px; text-align: left; }
    .star { color: var(--color-primary); display: inline-flex; flex-shrink: 0; }
    .pass-text { display: flex; flex-direction: column; line-height: 1.25; }
    .comp { font-size: 0.9rem; font-weight: 600; color: var(--color-primary); }
    .date { font-size: 0.8rem; color: var(--color-text-secondary); }
    .add { color: var(--color-text-secondary); opacity: 0.5; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
    .fields { display: flex; flex-direction: column; gap: 12px; }

    /* Mobile: drop the wide matrix for a stacked card list grouped by dance type. */
    .cards { display: none; }
    .dcard { border: 1px solid var(--color-border); border-radius: var(--radius-md, 8px); padding: 10px 12px; margin-bottom: 10px; }
    .dcard-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-weight: 600; margin-bottom: 6px; }
    .prow { display: flex; align-items: center; gap: 10px; padding: 6px 0; border-top: 1px solid var(--color-border); }
    .prow:first-of-type { border-top: none; }
    .prow.editable { cursor: pointer; }
    .lvl { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; color: var(--color-text-secondary); }
    .iconbtn { display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--color-border); background: var(--color-surface, transparent); border-radius: var(--radius-sm, 6px); padding: 4px; color: var(--color-primary); cursor: pointer; }
    .empty-type { color: var(--color-text-secondary); font-size: 0.85rem; padding: 2px 0; }
    @media (max-width: 640px) {
      .scroll { display: none; }
      .cards { display: block; }
      .head { flex-wrap: wrap; }
    }
  `;

  connectedCallback(): void { super.connectedCallback(); this.load(); }

  updated(changed: Map<string, unknown>) {
    if (changed.has('studentId') && this.studentId) this.load();
  }

  private get canEdit() { return store.hasPermission('students.edit'); }

  private async load() {
    if (!this.studentId) return;
    this.loading = true;
    try {
      const [types, levels, passes] = await Promise.all([
        api.get<Ref[]>('/dance/types'),
        api.get<Ref[]>('/dance/levels'),
        api.get<Pass[]>(`/modules/irish-dance/passes/${this.studentId}`),
      ]);
      this.types = types; this.levels = levels; this.passes = passes;
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private passAt(typeId: string, levelId: string): Pass | undefined {
    return this.passes.find((p) => p.dance_type_id === typeId && p.level_id === levelId);
  }

  private openCell(typeId: string, levelId: string) {
    if (!this.canEdit) return;
    const existing = this.passAt(typeId, levelId);
    this.form = existing
      ? { id: existing.id, dance_type_id: existing.dance_type_id, level_id: existing.level_id, competition_name: existing.competition_name, competition_date: existing.competition_date, notes: existing.notes ?? '' }
      : { dance_type_id: typeId, level_id: levelId, competition_name: '', competition_date: '', notes: '' };
  }

  private openNew() {
    if (!this.canEdit) return;
    this.form = { dance_type_id: this.types[0]?.id ?? '', level_id: this.levels[0]?.id ?? '', competition_name: '', competition_date: '', notes: '' };
  }

  private setField(k: keyof PassForm, v: string) { if (this.form) this.form = { ...this.form, [k]: v }; }

  private async save() {
    if (!this.form) return;
    const f = this.form;
    if (!f.dance_type_id || !f.level_id) { showToast(t('irishDance.passes.errTypeLevel'), 'error'); return; }
    if (!f.competition_name.trim()) { showToast(t('irishDance.passes.errCompetition'), 'error'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.competition_date)) { showToast(t('irishDance.passes.errDate'), 'error'); return; }
    this.saving = true;
    try {
      const body = { dance_type_id: f.dance_type_id, level_id: f.level_id, competition_name: f.competition_name.trim(), competition_date: f.competition_date, notes: f.notes.trim() };
      if (f.id) await api.put(`/modules/irish-dance/passes/${f.id}`, body);
      else await api.post(`/modules/irish-dance/passes/${this.studentId}`, body);
      this.form = null;
      await this.load();
      showToast(t('irishDance.toast.saved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  private async deletePassRow() {
    if (!this.form?.id) return;
    if (!confirm(t('irishDance.passes.confirmDelete'))) return;
    this.saving = true;
    try {
      await api.del(`/modules/irish-dance/passes/${this.form.id}`);
      this.form = null;
      await this.load();
      showToast(t('irishDance.toast.saved'), 'success');
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.saving = false; }
  }

  render() {
    return html`
      <cadence-card heading=${t('irishDance.passes.heading')}>
        <div class="head">
          <span class="muted">${t('irishDance.passes.lede')}</span>
          ${this.canEdit ? html`<cadence-button @click=${() => this.openNew()}>${icon('plus', 16)} ${t('irishDance.passes.add')}</cadence-button>` : ''}
        </div>
        ${this.loading
          ? html`<p class="muted">${t('common.states.loading')}</p>`
          : this.types.length === 0 || this.levels.length === 0
            ? html`<cadence-empty-state heading=${t('irishDance.passes.noConfig')}></cadence-empty-state>`
            : html`${this.renderMatrix()}${this.renderCards()}`}
      </cadence-card>
      ${this.form ? this.renderModal() : ''}
    `;
  }

  private renderMatrix() {
    return html`
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th class="rowhead"></th>
              ${this.levels.map((l) => html`<th>${l.name}</th>`)}
            </tr>
          </thead>
          <tbody>
            ${this.types.map((ty) => html`
              <tr>
                <td class="rowhead">${ty.name}</td>
                ${this.levels.map((l) => {
                  const p = this.passAt(ty.id, l.id);
                  return html`
                    <td class="cell ${this.canEdit ? 'editable' : ''}" title=${p ? `${p.competition_name} · ${p.competition_date}` : ''}
                      @click=${() => this.openCell(ty.id, l.id)}>
                      ${p
                        ? html`<span class="pass"><span class="star">${icon('award', 18)}</span><span class="pass-text"><span class="comp">${p.competition_name}</span><span class="date">${p.competition_date}</span></span></span>`
                        : this.canEdit ? html`<span class="add">${icon('plus', 14)}</span>` : ''}
                    </td>`;
                })}
              </tr>`)}
          </tbody>
        </table>
      </div>
    `;
  }

  private levelName(levelId: string): string {
    return this.levels.find((l) => l.id === levelId)?.name ?? '';
  }

  private openForType(typeId: string) {
    if (!this.canEdit) return;
    this.form = { dance_type_id: typeId, level_id: this.levels[0]?.id ?? '', competition_name: '', competition_date: '', notes: '' };
  }

  /** Narrow-screen alternative to the matrix: one card per dance type, listing its passes. */
  private renderCards() {
    const anyPasses = this.passes.length > 0;
    return html`
      <div class="cards">
        ${!anyPasses && !this.canEdit
          ? html`<p class="muted">${t('irishDance.passes.noPasses')}</p>`
          : this.types
              .map((ty) => ({ ty, list: this.passes.filter((p) => p.dance_type_id === ty.id) }))
              .filter(({ list }) => list.length > 0 || this.canEdit)
              .map(({ ty, list }) => html`
                <div class="dcard">
                  <div class="dcard-head">
                    <span>${ty.name}</span>
                    ${this.canEdit ? html`<button class="iconbtn" aria-label=${t('irishDance.passes.add')} @click=${() => this.openForType(ty.id)}>${icon('plus', 16)}</button>` : ''}
                  </div>
                  ${list.length
                    ? list.map((p) => html`
                        <div class="prow ${this.canEdit ? 'editable' : ''}" @click=${() => this.openCell(p.dance_type_id, p.level_id)}>
                          <span class="star">${icon('award', 18)}</span>
                          <span class="pass-text">
                            <span class="lvl">${this.levelName(p.level_id)}</span>
                            <span class="comp">${p.competition_name}</span>
                            <span class="date">${p.competition_date}</span>
                          </span>
                        </div>`)
                    : html`<div class="empty-type">${t('irishDance.passes.noPasses')}</div>`}
                </div>`)}
      </div>
    `;
  }

  private renderModal() {
    const f = this.form!;
    return html`
      <cadence-modal open heading=${f.id ? t('irishDance.passes.editTitle') : t('irishDance.passes.addTitle')} @close=${() => (this.form = null)}>
        <div class="fields">
          <cadence-select label=${t('irishDance.passes.danceType')} .value=${f.dance_type_id}
            .options=${this.types.map((ty) => ({ value: ty.id, label: ty.name }))}
            @change=${(e: CustomEvent) => this.setField('dance_type_id', e.detail)}></cadence-select>
          <cadence-select label=${t('irishDance.passes.level')} .value=${f.level_id}
            .options=${this.levels.map((l) => ({ value: l.id, label: l.name }))}
            @change=${(e: CustomEvent) => this.setField('level_id', e.detail)}></cadence-select>
          <cadence-input label=${t('irishDance.passes.competition')} .value=${f.competition_name}
            @input=${(e: CustomEvent) => this.setField('competition_name', e.detail)}></cadence-input>
          <cadence-input label=${t('irishDance.passes.date')} type="date" .value=${f.competition_date}
            @input=${(e: CustomEvent) => this.setField('competition_date', e.detail)}></cadence-input>
          <cadence-input label=${t('irishDance.passes.notes')} .value=${f.notes}
            @input=${(e: CustomEvent) => this.setField('notes', e.detail)}></cadence-input>
        </div>
        <div slot="footer">
          ${f.id ? html`<cadence-button variant="ghost" @click=${() => this.deletePassRow()} ?disabled=${this.saving}>${t('irishDance.actions.delete')}</cadence-button>` : ''}
          <cadence-button variant="ghost" @click=${() => (this.form = null)}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button @click=${() => this.save()} ?disabled=${this.saving}>${t('common.actions.save')}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
